/**
 * Utilities for interoperating with legacy store.js (storageHandler) localStorage values.
 *
 * store.js auto-JSON-serialises on set and auto-parses on get.
 * Legacy code follows the pattern:
 *
 *   storageHandler.set(key, JSON.stringify(value))   → raw localStorage = JSON.stringify(JSON.stringify(value))
 *   JSON.parse(storageHandler.get(key))              → storageHandler.get parses once, JSON.parse parses again
 *
 * So the raw localStorage value is always double-stringified.
 * These helpers match that contract so React and legacy code can safely share the same keys.
 */

/**
 * Read a value written by legacy store.js from raw localStorage.
 * Handles both single- and double-stringified values for tolerance.
 * Returns `null` if the key does not exist or cannot be parsed.
 */
export function legacyStorageGet<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    const first = JSON.parse(raw);
    // If the result is still a string, it may be double-stringified — try parsing again.
    // Fall back to the string itself if the second parse fails (e.g. single-stringified string value).
    if (typeof first === "string") {
      try {
        return JSON.parse(first);
      } catch {
        return first as T;
      }
    }
    return first as T;
  } catch {
    return null;
  }
}

/**
 * Write a value to raw localStorage in the format expected by legacy store.js.
 * Equivalent to: storageHandler.set(key, JSON.stringify(value))
 */
export function legacyStorageSet<T>(key: string, value: T): void {
  localStorage.setItem(key, JSON.stringify(JSON.stringify(value)));
}

/**
 * Remove a key from raw localStorage (same effect as storageHandler.remove).
 */
export function legacyStorageRemove(key: string): void {
  localStorage.removeItem(key);
}

/**
 * Check whether a key exists in raw localStorage (same as storageHandler.exists).
 */
export function legacyStorageExists(key: string): boolean {
  return localStorage.getItem(key) !== null;
}
